import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  getChartVolumeAndPriceByToday,
  stockInformationDataService,
  shortcutChartDataService,
} from 'services/analyticsCenter/StockInformation';
import {
  ShortcutChartDataTypes,
  ShortcutChartParamsTypes,
  StockInformationDataTypes,
  ChartVolumeAndPriceByTodayTypes,
  ChartVolumeAndPriceByTodayParamsTypes,
  PriceBaseDataTypes,
} from 'services/analyticsCenter/StockInformation/types';
import matchingHistoryDataService from 'services/analyticsCenter/matchingHistory';
import {
  MatchingHistoryTypes,
  MatchingHistoryParamsTypes,
  ItemMatchingHistoryTypes,
} from 'services/analyticsCenter/matchingHistory/types';
import getWsPriceboardService from 'services/wsPriceboard';
import {
  getDateInfoArrayYYYYMMDD,
  isSameDateString,
  parseStringToNumber,
} from 'utils/functions';

type StockInformationState = {
  priceBase?: PriceBaseDataTypes;
  capitalRasingHistoryList?: StockInformationDataTypes;
  chartVolumeByTodayList: ChartVolumeAndPriceByTodayTypes[];
  shortcutChartData: ShortcutChartDataTypes[];
  shortcutChartDataPast: ShortcutChartDataTypes[];
  loadingCapitalRasingHistoryList: boolean;
  matchingHistoryList: ItemMatchingHistoryTypes[];
  loadingMatchingHistory: boolean;
  loadingPriceboard: boolean;
};

const initialState: StockInformationState = {
  chartVolumeByTodayList: [],
  shortcutChartData: [],
  shortcutChartDataPast: [],
  loadingCapitalRasingHistoryList: false,
  matchingHistoryList: [],
  loadingMatchingHistory: false,
  loadingPriceboard: false,
};

export const getStockInformationAction = createAsyncThunk<
  StockInformationDataTypes,
  string,
  { rejectValue: ErrorResponse }
>(
  'stockInformationReducer/getStockInformation',
  async (code: string, { rejectWithValue }) => {
    try {
      return await stockInformationDataService(code);
    } catch (error) {
      return rejectWithValue(error as ErrorResponse);
    }
  },
);

export const getChartVolumeAndPriceByTodayAction = createAsyncThunk<
  ChartVolumeAndPriceByTodayTypes[],
  ChartVolumeAndPriceByTodayParamsTypes,
  { rejectValue: ErrorResponse }
>(
  'stockInformationReducer/getChartVolumeAndPriceByTodayAction',
  async (
    params: ChartVolumeAndPriceByTodayParamsTypes,
    { rejectWithValue },
  ) => {
    try {
      return await getChartVolumeAndPriceByToday(params);
    } catch (error) {
      return rejectWithValue(error as ErrorResponse);
    }
  },
);

export const getMatchingHistoryAction = createAsyncThunk<
  ItemMatchingHistoryTypes[],
  MatchingHistoryParamsTypes,
  { rejectValue: ErrorResponse }
>(
  'stockInformationReducer/getMatchingHistory',
  async (params: MatchingHistoryParamsTypes, { rejectWithValue }) => {
    try {
      return await matchingHistoryDataService(params);
    } catch (error) {
      return rejectWithValue(error as ErrorResponse);
    }
  },
);
export const getShortcutChartDataAction = createAsyncThunk<
  ShortcutChartDataTypes[],
  ShortcutChartParamsTypes,
  { rejectValue: ErrorResponse }
>(
  'stockInformationReducer/getShortcutChartData',
  async (params: ShortcutChartParamsTypes, { rejectWithValue }) => {
    try {
      return await shortcutChartDataService(params);
    } catch (error) {
      return rejectWithValue(error as ErrorResponse);
    }
  },
);

export const getPriceboardStockInfoAction = createAsyncThunk<
  SocketResponseTypes,
  string,
  { rejectValue: ErrorResponse }
>(
  'stockInformationReducer/getPriceboardStockInfoAction',
  async (stockCode, { rejectWithValue }) => {
    try {
      return await getWsPriceboardService({
        selectedStocks: stockCode,
        criteriaId: '',
        marketId: 0,
        lastSeq: 0,
        isReqTL: true,
        isReqMK: false,
        tlSymbol: stockCode,
        pthMktId: '',
        isGetNullValue: false,
        isIncrValue: true,
      });
    } catch (error) {
      return rejectWithValue(error as ErrorResponse);
    }
  },
);

const mappingPriceboardToShortcut = (
  stock: StockInfoTypes,
  tlfData: TimeMatchingTypes[],
): ShortcutChartDataTypes[] => {
  const td = stock.TD.split('/');
  return tlfData.map((item) => ({
    price: parseStringToNumber(item.f.i[1]),
    volume: parseStringToNumber(item.f.i[3]),
    priceChangePercent:
      (parseStringToNumber(item.f.i[2]) / Number(stock.RE)) * 100,
    priceChange: parseStringToNumber(item.f.i[2]),
    sessionDate: `${td[2]}/${td[1]}/${td[0]} ${item.f.i[0]}`,
  }));
};

const mappingPriceboardToCapitalRasing = (
  stock: StockInfoTypes,
  oldState?: StockInformationDataTypes,
): StockInformationDataTypes => ({
  ...oldState,
  priceReference: parseStringToNumber(stock.RE),
  priceOpen: parseStringToNumber(stock.X),
  priceLowest: parseStringToNumber(stock.Z),
  priceHighest: parseStringToNumber(stock.Y),
  matchingVolume: parseStringToNumber(stock.Q),
  matchingValue: parseStringToNumber(stock.R),
  volumeForeignSell: parseStringToNumber(stock.T),
  volumeForeign: parseStringToNumber(stock.S),
  percentForeign: parseStringToNumber(stock.V),
});

const reduceChartPriceData = (
  curentData: ChartVolumeAndPriceByTodayTypes[],
  data: ChartVolumeAndPriceByTodayTypes[],
) => {
  const resultData: ChartVolumeAndPriceByTodayTypes[] = [...curentData, ...data];
  const result = resultData
    .map((item) => ({ ...item, price: item.price }))
    .reduce(
      (oldGroups, item) => ({
        ...oldGroups,
        [item.price]: {
          ...item,
          volume: (Number(oldGroups[item.price]?.volume || 0) + Number(item.volume)).toString(),
        },
      }),
      {} as Record<string, ChartVolumeAndPriceByTodayTypes>,
    );
  return Object.values(result).sort((a, b) => Number(a.price) - Number(b.price));
};

const mappingPriceboardToChartPrice = (
  stock: StockInfoTypes,
  tlfData: TimeMatchingTypes[],
): ChartVolumeAndPriceByTodayTypes[] => {
  const mappingData = tlfData.map((item) => ({
    price: String(item.f.i[1]),
    volume: String(item.f.i[3]),
    volumeAccumulate: String(stock.Q),
  }));

  return reduceChartPriceData([], mappingData);
};

const mappingPriceboardToMatchingData = (
  stock: StockInfoTypes,
  tlfData: TimeMatchingTypes[],
): ItemMatchingHistoryTypes[] => {
  const td = stock.TD.split('/');
  return tlfData
    .map((item) => ({
      price: Number(item.f.i[1]),
      volume: Number(item.f.i[3]),
      sessionDate: `${td[2]}/${td[1]}/${td[0]} ${item.f.i[0]}`,
      priceChange: Number(item.f.i[2]),
      priceChangePercent: (Number(item.f.i[2]) / Number(stock.RE)) * 100,
    }))
    .reverse();
};

const socketUpdateChartPrice = (
  oldData: ChartVolumeAndPriceByTodayTypes[], newData: ChartVolumeAndPriceByTodayTypes[],
) => {
  const result = oldData.slice();
  newData.forEach((element) => {
    const index = result.findIndex((item) => item.price === element.price);
    if (index > -1) {
      result[index] = {
        ...result[index],
        volume: (Number(result[index].volume) + Number(element.volume)).toString(),
      };
    } else {
      result.push(element);
    }
  });
  return result.sort((a, b) => Number(a.price) - Number(b.price));
};

export const stockInformationSlice = createSlice({
  name: 'stockInformationReducer',
  initialState,
  reducers: {
    resetDataStock: ($state) => {
      $state.priceBase = undefined;
      $state.capitalRasingHistoryList = undefined;
      $state.chartVolumeByTodayList = [];
      $state.shortcutChartData = [];
      $state.shortcutChartDataPast = [];
      $state.matchingHistoryList = [];
    },

    updateMatchingHistoryList: (
      $state,
      action: PayloadAction<MatchingHistoryTypes>,
    ) => {
      $state.matchingHistoryList = [
        ...$state.matchingHistoryList,
        ...action.payload.items,
      ];
    },
    updateStockInformationSocket: (
      $state,
      action: PayloadAction<{
        stock: StockInfoTypes;
        time: TimeMatchingTypes[];
      }>,
    ) => {
      const { stock, time } = { ...action.payload };
      const td = stock ? stock.TD.split('/') : getDateInfoArrayYYYYMMDD();

      const isToday = $state.shortcutChartData.length
        && $state.shortcutChartData[0].sessionDate
        ? isSameDateString(
          $state.shortcutChartData[0].sessionDate,
          `${td[2]}/${td[1]}/${td[0]} ${time[0].f.i[0]}`,
        )
        : false;

      if (isToday) {
        $state.shortcutChartData = $state.shortcutChartData.concat(
          time.map((item) => ({
            price: Number(item.f.i[1]),
            volume: Number(item.f.i[3]),
            priceChangePercent: (Number(item.f.i[2]) / parseStringToNumber(stock.RE)) * 100,
            priceChange: Number(item.f.i[2]),
            sessionDate: `${td[2]}/${td[1]}/${td[0]} ${item.f.i[0]}`,
          })),
        );
      }

      // Price base

      $state.priceBase = {
        price: stock.H || stock.RE,
        priceChange: stock.J,
        rate: ((parseStringToNumber(stock.J) / parseStringToNumber(stock.RE)) * 100).toFixed(2),
      };

      // Stock information
      const capitalClone = { ...$state.capitalRasingHistoryList };
      const newData: StockInformationDataTypes = {
        ...capitalClone,
        priceReference: parseStringToNumber(stock.RE || capitalClone.priceReference),
        priceOpen: parseStringToNumber(stock.X || capitalClone.priceOpen),
        priceLowest: parseStringToNumber(stock.Z || capitalClone.priceLowest),
        priceHighest: parseStringToNumber(stock.Y || capitalClone.priceHighest),
        matchingVolume: parseStringToNumber(stock.Q || capitalClone.matchingVolume),
        matchingValue: parseStringToNumber(stock.R || capitalClone.matchingValue),
        volumeForeignSell: parseStringToNumber(stock.T || capitalClone.volumeForeignSell),
        volumeForeign: parseStringToNumber(stock.S || capitalClone.volumeForeign),
        percentForeign: parseStringToNumber(stock.V || capitalClone.percentForeign),
      };
      $state.capitalRasingHistoryList = newData;

      // chartVolumeByTodayList
      $state.chartVolumeByTodayList = socketUpdateChartPrice(
        $state.chartVolumeByTodayList,
        time.map((item) => ({
          price: String(item.f.i[1]),
          volume: String(item.f.i[3]),
          volumeAccumulate: String(stock.Q),
        })),
      );

      // Matching History
      const newDataMatching: ItemMatchingHistoryTypes[] = time.map((item) => ({
        price: Number(item.f.i[1]),
        volume: Number(item.f.i[3]),
        sessionDate: `${td[2]}/${td[1]}/${td[0]} ${item.f.i[0]}`,
        priceChange: Number(item.f.i[2]),
        priceChangePercent: (Number(item.f.i[2]) / Number(stock.RE)) * 100,
      }));
      $state.matchingHistoryList = newDataMatching.concat(
        $state.matchingHistoryList,
      );
    },
  },
  extraReducers(builder) {
    builder.addCase(getStockInformationAction.pending, ($state) => {
      $state.loadingCapitalRasingHistoryList = true;
    });
    builder.addCase(getStockInformationAction.fulfilled, ($state, action) => {
      const { payload } = action;
      $state.capitalRasingHistoryList = {
        ...$state.capitalRasingHistoryList,
        exchangeCode: payload.exchangeCode,
        stockSymbol: payload.stockSymbol,
        icbCode: payload.icbCode,
        priceChange: payload.priceChange,
        priceChangePercent: payload.priceChangePercent,
        volumeAvg20Days: payload.volumeAvg20Days,
        beta: payload.beta,
        charteredCapital: payload.charteredCapital,
        marketCapital: payload.marketCapital,
        marketCapitalRank: payload.marketCapitalRank,
        pE: payload.pE,
        eps: payload.eps,
        pB: payload.pB,
        sharesOutstanding: payload.sharesOutstanding,
        bookValue: payload.bookValue,
        priceHighest52w: payload.priceHighest52w,
        priceLowest52w: payload.priceLowest52w,
        roa: payload.roa,
        roe: payload.roe,
      };
      $state.loadingCapitalRasingHistoryList = false;
    });
    builder.addCase(getStockInformationAction.rejected, ($state) => {
      $state.loadingCapitalRasingHistoryList = false;
    });
    builder.addCase(
      getChartVolumeAndPriceByTodayAction.fulfilled,
      ($state, action) => {
        $state.chartVolumeByTodayList = action.payload;
      },
    );
    // getMatchingHistoryAction
    builder.addCase(getMatchingHistoryAction.pending, ($state) => {
      $state.loadingMatchingHistory = true;
    });
    builder.addCase(getMatchingHistoryAction.fulfilled, ($state, action) => {
      $state.matchingHistoryList = action.payload;
      $state.loadingMatchingHistory = false;
    });
    builder.addCase(getMatchingHistoryAction.rejected, ($state) => {
      $state.loadingMatchingHistory = false;
    });
    builder.addCase(getShortcutChartDataAction.fulfilled, ($state, action) => {
      $state.shortcutChartDataPast = action.payload;
    });

    // priceboard
    builder.addCase(getPriceboardStockInfoAction.pending, ($state) => {
      $state.loadingPriceboard = true;
    });
    builder.addCase(
      getPriceboardStockInfoAction.fulfilled,
      ($state, action) => {
        $state.capitalRasingHistoryList = undefined;
        if (action.payload.pb
          && action.payload.pb.f
          && action.payload.pb.f.length) {
          const stock = JSON.parse(action.payload.pb.f[0]) as StockInfoTypes;
          $state.priceBase = {
            price: stock.H || stock.RE,
            priceChange: stock.J,
            rate: ((parseStringToNumber(stock.J) / parseStringToNumber(stock.RE)) * 100).toFixed(2),
          };
        }
        if (
          action.payload.pb
          && action.payload.pb.f
          && action.payload.pb.f.length
          && action.payload.tl
          && action.payload.tl.f
          && action.payload.tl.f.length
        ) {
          const stock = JSON.parse(action.payload.pb.f[0]) as StockInfoTypes;
          const tlfData = action.payload.tl.f;

          $state.shortcutChartData = mappingPriceboardToShortcut(
            stock,
            tlfData,
          );

          $state.capitalRasingHistoryList = mappingPriceboardToCapitalRasing(
            stock,
            $state.capitalRasingHistoryList,
          );

          $state.chartVolumeByTodayList = mappingPriceboardToChartPrice(
            stock,
            tlfData,
          );

          $state.matchingHistoryList = mappingPriceboardToMatchingData(
            stock,
            tlfData,
          );
        }
        $state.loadingPriceboard = false;
      },
    );
    builder.addCase(getPriceboardStockInfoAction.rejected, ($state) => {
      $state.loadingPriceboard = false;
    });
  },
});

export const {
  resetDataStock,
  updateStockInformationSocket,
  updateMatchingHistoryList,
} = stockInformationSlice.actions;

export default stockInformationSlice.reducer;
